import React, { useState, useRef } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { User, Bell, Shield, Building, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

function Toast({ message, type = 'success' }) {
    return (
        <div className={`fixed bottom-6 right-6 z-[200] flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl border text-sm font-medium animate-in slide-in-from-bottom-4 duration-300
            ${type === 'success' ? 'bg-white border-green-200 text-green-700' : 'bg-white border-red-200 text-red-700'}`}>
            {type === 'success'
                ? <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                : <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />}
            {message}
        </div>
    );
}

export default function Settings() {
    const { user, logout } = useAuth();
    const avatarInputRef = useRef(null);
    const [avatarSrc, setAvatarSrc] = useState(null);
    const [formName, setFormName] = useState(user?.name || '');
    const [formEmail, setFormEmail] = useState(user?.email || 'user@example.com');
    const [toast, setToast] = useState(null);

    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');

    const [institutionName, setInstitutionName] = useState('P2G Educational Trust');
    const [contactEmail, setContactEmail] = useState('admin@p2g.edu');
    const [notifSettings, setNotifSettings] = useState({ 0: true, 1: true, 2: false });

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        setAvatarSrc(url);
        showToast('Avatar updated successfully!');
    };

    const handleSaveProfile = () => {
        if (!formName.trim()) { showToast('Name cannot be empty.', 'error'); return; }
        showToast('Profile details saved successfully!');
    };

    const handleChangePassword = () => {
        if (!currentPw) { showToast('Please enter your current password.', 'error'); return; }
        if (newPw.length < 6) { showToast('New password must be at least 6 characters.', 'error'); return; }
        if (newPw !== confirmPw) { showToast('Passwords do not match.', 'error'); return; }
        setCurrentPw(''); setNewPw(''); setConfirmPw('');
        showToast('Password changed successfully!');
    };

    const handleLogoutAll = () => {
        if (window.confirm('Are you sure? You will be logged out from all devices.')) {
            showToast('Logging out from all devices...');
            setTimeout(() => logout(), 1500);
        }
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6 pb-12">
            <div>
                <h1 className="text-2xl font-bold text-slate-900">Platform Settings</h1>
                <p className="text-sm text-slate-500">Manage your account credentials, security preferences, and institution details.</p>
            </div>

            <div className="flex flex-col md:flex-row gap-8 mt-6 lg:items-start">
                <div className="w-full md:w-64 shrink-0">
                    <Tabs defaultValue="account" orientation="vertical" className="w-full">
                        <TabsList className="flex flex-row md:flex-col bg-transparent w-full gap-1 h-auto overflow-x-auto p-0 rounded-none border-b md:border-b-0 border-slate-200">
                            <TabsTrigger value="account" className="justify-start px-4 py-3 data-[state=active]:bg-brand-50 data-[state=active]:text-brand-700 data-[state=active]:shadow-none rounded-xl border border-transparent whitespace-nowrap">
                                <User className="mr-3 h-4 w-4" /> Account
                            </TabsTrigger>
                            <TabsTrigger value="notifications" className="justify-start px-4 py-3 data-[state=active]:bg-brand-50 data-[state=active]:text-brand-700 data-[state=active]:shadow-none rounded-xl border border-transparent whitespace-nowrap">
                                <Bell className="mr-3 h-4 w-4" /> Notifications
                            </TabsTrigger>
                            <TabsTrigger value="security" className="justify-start px-4 py-3 data-[state=active]:bg-brand-50 data-[state=active]:text-brand-700 data-[state=active]:shadow-none rounded-xl border border-transparent whitespace-nowrap">
                                <Shield className="mr-3 h-4 w-4" /> Security
                            </TabsTrigger>
                            <TabsTrigger value="institution" className="justify-start px-4 py-3 data-[state=active]:bg-brand-50 data-[state=active]:text-brand-700 data-[state=active]:shadow-none rounded-xl border border-transparent whitespace-nowrap">
                                <Building className="mr-3 h-4 w-4" /> Institution
                            </TabsTrigger>
                        </TabsList>

                        <div className="mt-6 md:absolute md:left-80 md:right-8 lg:right-auto lg:w-[calc(100%-24rem)] md:top-24">
                            {/* Account Tab */}
                            <TabsContent value="account" className="mt-0 space-y-6">
                                <Card className="shadow-sm border border-slate-200">
                                    <CardHeader>
                                        <CardTitle>Profile Details</CardTitle>
                                        <CardDescription>Update your personal information and public profile.</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="flex items-center gap-6 mb-6">
                                            <div
                                                className="h-20 w-20 rounded-full bg-slate-100 flex items-center justify-center text-2xl font-bold text-slate-400 border-2 border-dashed border-slate-300 overflow-hidden cursor-pointer hover:border-brand-400 transition-colors"
                                                onClick={() => avatarInputRef.current?.click()}
                                                title="Click to change avatar"
                                            >
                                                {avatarSrc
                                                    ? <img src={avatarSrc} alt="Avatar" className="w-full h-full object-cover" />
                                                    : (user?.name?.charAt(0) || 'U')
                                                }
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <Button variant="outline" size="sm" onClick={() => avatarInputRef.current?.click()}>
                                                    Change Avatar
                                                </Button>
                                                <p className="text-xs text-slate-400">JPG, PNG or GIF. Max 2MB.</p>
                                            </div>
                                            <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                                        </div>
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                                                <Input value={formName} onChange={e => setFormName(e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">Role / Title</label>
                                                <Input readOnly value={user?.role || 'Therapist'} className="bg-slate-50 text-slate-500" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Primary Email</label>
                                            <Input type="email" value={formEmail} onChange={e => setFormEmail(e.target.value)} />
                                        </div>
                                    </CardContent>
                                    <CardFooter className="border-t border-slate-100 bg-slate-50 justify-end py-4">
                                        <Button onClick={handleSaveProfile}><Save className="mr-2 h-4 w-4" /> Save Changes</Button>
                                    </CardFooter>
                                </Card>
                            </TabsContent>

                            {/* Notifications Tab */}
                            <TabsContent value="notifications" className="mt-0 space-y-6">
                                <Card className="shadow-sm border border-slate-200">
                                    <CardHeader>
                                        <CardTitle>Email Notifications</CardTitle>
                                        <CardDescription>Configure when you receive email alerts.</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        {[
                                            { title: 'Goal Mastery Alerts', desc: 'Receive an email when a student achieves 100% on a goal' },
                                            { title: 'Assessment Submissions', desc: 'Notify when a parent or peer completes a requested form' },
                                            { title: 'System Updates', desc: 'Platform maintenance and feature announcements' },
                                        ].map((setting, i) => (
                                            <div key={i} className="flex items-start justify-between py-3 border-b border-slate-100 last:border-0">
                                                <div>
                                                    <h4 className="text-sm font-medium text-slate-900">{setting.title}</h4>
                                                    <p className="text-xs text-slate-500 mt-1">{setting.desc}</p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only peer"
                                                        checked={!!notifSettings[i]}
                                                        onChange={e => setNotifSettings(s => ({ ...s, [i]: e.target.checked }))}
                                                    />
                                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-500"></div>
                                                </label>
                                            </div>
                                        ))}
                                    </CardContent>
                                    <CardFooter className="border-t border-slate-100 bg-slate-50 justify-end py-4">
                                        <Button onClick={() => showToast('Notification preferences updated!')}><Save className="mr-2 h-4 w-4" /> Update Preferences</Button>
                                    </CardFooter>
                                </Card>
                            </TabsContent>

                            {/* Security Tab */}
                            <TabsContent value="security" className="mt-0 space-y-6">
                                <Card className="shadow-sm border border-slate-200">
                                    <CardHeader>
                                        <CardTitle>Security &amp; Access</CardTitle>
                                        <CardDescription>Manage your password and active sessions.</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
                                            <Input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder="Enter current password" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                                            <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="At least 6 characters" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
                                            <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Repeat new password" />
                                        </div>
                                    </CardContent>
                                    <CardFooter className="border-t border-slate-100 bg-slate-50 justify-between py-4">
                                        <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleLogoutAll}>
                                            Log out of all devices
                                        </Button>
                                        <Button onClick={handleChangePassword}><Save className="mr-2 h-4 w-4" /> Change Password</Button>
                                    </CardFooter>
                                </Card>
                            </TabsContent>

                            {/* Institution Tab */}
                            <TabsContent value="institution" className="mt-0 space-y-6">
                                <Card className="shadow-sm border border-slate-200">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2"><Building className="h-5 w-5 text-brand-600" /> Workspace Settings</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Clinic / Institution Name</label>
                                            <Input value={institutionName} onChange={e => setInstitutionName(e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-1">Contact Email</label>
                                            <Input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
                                        </div>
                                    </CardContent>
                                    <CardFooter className="border-t border-slate-100 bg-slate-50 justify-end py-4">
                                        <Button onClick={() => showToast('Institution details saved!')}><Save className="mr-2 h-4 w-4" /> Save Details</Button>
                                    </CardFooter>
                                </Card>
                            </TabsContent>
                        </div>
                    </Tabs>
                </div>
            </div>

            <div className="h-80 md:hidden"></div>

            {toast && <Toast message={toast.msg} type={toast.type} />}
        </div>
    );
}
